/**
 * useRealtime -- WebSocket connection to /ws/feed.
 * Subscribes to Redis-backed agent feed for live agent results,
 * trade executions, recommendations, and alerts.
 *
 * Maintains backwards-compatible fields (portfolioValue, marketTick, etc.)
 * consumed by Shell, DashboardPage, MarketsPage, AgentsPage, ActivityFeed.
 */
import { useEffect, useRef, useCallback } from 'react'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { AgentFeedMessage, AgentFeedType, AgentStatus } from '@/lib/types'

const _loc = typeof window !== 'undefined' ? window.location : { protocol: 'http:', host: 'localhost:8000' }
const WS_URL = import.meta.env.VITE_WS_URL ?? `${_loc.protocol === 'https:' ? 'wss:' : 'ws:'}//${_loc.host}`

// ── Reconnect backoff config ─────────────────────────────────────────────────
const INITIAL_RECONNECT_MS = 1_000
const MAX_RECONNECT_MS = 30_000
const BACKOFF_MULTIPLIER = 1.5

interface RealtimeState {
  connected: boolean

  // New: structured agent feed messages
  messages: AgentFeedMessage[]

  // Legacy fields (kept for backwards compatibility with existing components)
  portfolioValue: number | null
  dailyChange: number | null
  dailyChangePct: number | null
  marketTick: Record<string, number>
  agentEvents: AgentStatus[]
  lastTimestamp: string | null

  // Internal setters
  _pushMessage: (msg: AgentFeedMessage) => void
  _setConnected: (connected: boolean) => void
}

export const useRealtimeStore = create<RealtimeState>()((set) => ({
  connected: false,
  messages: [],
  portfolioValue: null,
  dailyChange: null,
  dailyChangePct: null,
  marketTick: {},
  agentEvents: [],
  lastTimestamp: null,

  _pushMessage: (msg) =>
    set((state) => {
      const messages = [...state.messages, msg].slice(-200)

      // Extract legacy fields from fallback-format messages (data.portfolio, data.agents, etc.)
      const data = msg.data ?? {}
      const updates: Partial<RealtimeState> = {
        messages,
        lastTimestamp: msg.timestamp ?? state.lastTimestamp,
      }

      // If message contains legacy portfolio data (from fallback WebSocket mode)
      if (data.portfolio) {
        updates.portfolioValue = data.portfolio.value ?? state.portfolioValue
        updates.dailyChange = data.portfolio.daily_change ?? state.dailyChange
        updates.dailyChangePct = data.portfolio.daily_change_pct ?? state.dailyChangePct
      }

      // If message contains legacy market_tick
      if (data.market_tick) {
        updates.marketTick = data.market_tick
      }

      // If message contains legacy agents array (AgentStatus[])
      if (Array.isArray(data.agents) && data.agents.length > 0) {
        updates.agentEvents = data.agents
      }

      return updates
    }),

  _setConnected: (connected) => set({ connected }),
}))

/**
 * Hook -- call once in a top-level component (e.g. Shell).
 * Establishes the WebSocket to /ws/feed and keeps the store updated.
 */
export function useRealtime() {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelay = useRef(INITIAL_RECONNECT_MS)
  const { _pushMessage, _setConnected } = useRealtimeStore()

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    try {
      const ws = new WebSocket(`${WS_URL}/ws/feed`)
      wsRef.current = ws

      ws.onopen = () => {
        // First message MUST be the auth token — the backend closes
        // unauthenticated sockets with 4401.
        const token = localStorage.getItem('av-access-token')
        ws.send(JSON.stringify({ token }))
        _setConnected(true)
        reconnectDelay.current = INITIAL_RECONNECT_MS  // reset backoff on success
      }

      ws.onmessage = (event) => {
        try {
          const msg: AgentFeedMessage = JSON.parse(event.data)
          if (!msg.type) return  // ignore malformed
          _pushMessage(msg)
        } catch {
          // ignore unparseable messages
        }
      }

      ws.onclose = () => {
        _setConnected(false)
        // Reconnect with exponential backoff
        const delay = reconnectDelay.current
        reconnectDelay.current = Math.min(delay * BACKOFF_MULTIPLIER, MAX_RECONNECT_MS)
        reconnectTimer.current = setTimeout(connect, delay)
      }

      ws.onerror = () => {
        ws.close()
      }
    } catch {
      _setConnected(false)
      const delay = reconnectDelay.current
      reconnectDelay.current = Math.min(delay * BACKOFF_MULTIPLIER, MAX_RECONNECT_MS)
      reconnectTimer.current = setTimeout(connect, delay)
    }
  }, [_pushMessage, _setConnected])

  useEffect(() => {
    connect()
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
    }
  }, [connect])
}

// ── Convenience selectors (backwards-compatible) ────────────────────────────

export function useRealtimePortfolio() {
  // useShallow: the selector builds a fresh object each call — without shallow
  // equality every store update re-renders every consumer even when none of
  // these four fields changed.
  return useRealtimeStore(
    useShallow(s => ({
      value:         s.portfolioValue,
      dailyChange:   s.dailyChange,
      dailyChangePct: s.dailyChangePct,
      connected:     s.connected,
    })),
  )
}

export function useRealtimeMessages() {
  return useRealtimeStore((s) => s.messages)
}

export function useRealtimeByType(type: AgentFeedType) {
  return useRealtimeStore((s) => s.messages.filter((m) => m.type === type))
}
